import {
  CompleteCreatePlanningPeriodDocument,
  CompleteUpdatePlanningPeriodDocument,
  CreatePlanningPeriodDocument,
  DeletePlanningPeriodDocument,
  PlanningPeriodsDataCollectionDocument,
  PlanningPeriodDetailDocument,
  SendReviewPlanningPeriodDocument,
  UpdatePlanningPeriodDocument,
  type PlanningValueObjectsPeriodTypeEnum,
  type PlanningValueObjectsParticipantRoleEnum,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface UsePeriodsParams {
  companyId: string
}

const usePeriods = ({ companyId }: UsePeriodsParams) => {
  const [completeCreateMutation, { loading: completeCreating }] = useMutation(
    CompleteCreatePlanningPeriodDocument,
    {
      extractorFn: (result) => result.planningMutations.completeCreatePlanningPeriod,
      refetchQueries: [PlanningPeriodsDataCollectionDocument],
    }
  )

  const [completeUpdateMutation, { loading: completeUpdating }] = useMutation(
    CompleteUpdatePlanningPeriodDocument,
    {
      extractorFn: (result) => result.planningMutations.completeUpdatePlanningPeriod,
      refetchQueries: [PlanningPeriodsDataCollectionDocument, PlanningPeriodDetailDocument],
    }
  )

  const [createMutation, { loading: creating }] = useMutation(CreatePlanningPeriodDocument, {
    extractorFn: (result) => result.planningMutations.createPlanningPeriod,
    refetchQueries: [PlanningPeriodsDataCollectionDocument],
  })

  const [updateMutation, { loading: updating }] = useMutation(UpdatePlanningPeriodDocument, {
    extractorFn: (result) => result.planningMutations.updatePlanningPeriod,
    refetchQueries: [PlanningPeriodsDataCollectionDocument, PlanningPeriodDetailDocument],
  })

  const [deleteMutation, { loading: deleting }] = useMutation(DeletePlanningPeriodDocument, {
    extractorFn: (result) => result.planningMutations.deletePlanningPeriod,
    refetchQueries: [PlanningPeriodsDataCollectionDocument],
  })

  const [sendReviewMutation, { loading: sendingReview }] = useMutation(
    SendReviewPlanningPeriodDocument,
    {
      extractorFn: (result) => result.planningMutations.sendReviewPlanningPeriod,
      refetchQueries: [PlanningPeriodsDataCollectionDocument, PlanningPeriodDetailDocument],
    }
  )

  const loading =
    completeCreating || completeUpdating || creating || updating || deleting || sendingReview

  const showError = (message: string) => {
    app.setFlash({
      type: 'wrong',
      message,
    })
  }

  const create = async (params: {
    name: string
    startDate: string
    endDate: string
    periodType: PlanningValueObjectsPeriodTypeEnum
  }) => {
    const result = await createMutation({
      variables: {
        companyId,
        ...params,
      },
    })

    const handled = handleResult(result, () =>
      showError(i18n.t('planning.period_wizard.errors.create_period'))
    )

    return { success: handled.success, data: handled.success ? handled.data : undefined }
  }

  const update = async (params: {
    id: string
    name?: string
    startDate?: string
    endDate?: string
  }) => {
    const result = await updateMutation({
      variables: params,
    })

    const handled = handleResult(result, () =>
      showError(i18n.t('planning.periods.errors.update_period'))
    )

    return { success: handled.success, data: handled.success ? handled.data : undefined }
  }

  const sendReview = async (id: string) => {
    const result = await sendReviewMutation({
      variables: { id },
    })

    const handled = handleResult(result, () =>
      showError(i18n.t('planning.periods.errors.update_period'))
    )

    return { success: handled.success, data: handled.success ? handled.data : undefined }
  }

  const remove = async (id: string) => {
    const result = await deleteMutation({
      variables: { id },
    })

    const handled = handleResult(result, () =>
      showError(i18n.t('planning.periods.errors.delete_period'))
    )

    return { success: handled.success }
  }

  const completeCreate = async (params: {
    name: string
    startDate: string
    endDate: string
    periodType: PlanningValueObjectsPeriodTypeEnum
    plannerItems: Array<{ employeeId: string }>
    participantItems: Array<{ employeeId: string; role: PlanningValueObjectsParticipantRoleEnum }>
  }) => {
    const result = await completeCreateMutation({
      variables: {
        companyId,
        ...params,
      },
    })

    const handled = handleResult(result, () =>
      showError(i18n.t('planning.period_wizard.errors.create_period'))
    )

    return { success: handled.success, data: handled.success ? handled.data : undefined }
  }

  const completeUpdate = async (params: {
    id: string
    name?: string
    startDate?: string
    endDate?: string
    plannerItems: Array<{ employeeId: string }>
    participantItems: Array<{ employeeId: string; role: PlanningValueObjectsParticipantRoleEnum }>
  }) => {
    const result = await completeUpdateMutation({
      variables: {
        companyId,
        ...params,
      },
    })

    const handled = handleResult(result, () =>
      showError(i18n.t('planning.periods.errors.update_period'))
    )

    return { success: handled.success, data: handled.success ? handled.data : undefined }
  }

  return { create, completeCreate, completeUpdate, update, sendReview, remove, loading }
}

export default usePeriods
